import { useState } from 'react'
import { Clock, Sparkles, MessageCircle, Handshake, Target, Banknote, Scale, ClipboardList, Home } from 'lucide-react'

export default function StarRating({ value = 0, max = 5, interactive = false, onChange, size = '1rem' }) {
  const [hovered, setHovered] = useState(0)
  const stars = Array.from({ length: max }, (_, i) => i + 1)
  const display = interactive ? (hovered || value) : value

  return (
    <span style={{ display: 'inline-flex', gap: '.1rem', alignItems: 'center' }}>
      {stars.map(star => (
        <span
          key={star}
          onClick={() => interactive && onChange?.(star)}
          onMouseEnter={() => interactive && setHovered(star)}
          onMouseLeave={() => interactive && setHovered(0)}
          style={{
            fontSize: size,
            cursor: interactive ? 'pointer' : 'default',
            color: star <= Math.round(display) ? '#f59e0b' : 'rgba(255,255,255,.2)',
            transition: 'color .1s',
            lineHeight: 1,
          }}
        >
          ★
        </span>
      ))}
    </span>
  )
}

/** Compact display: ★ 4.3 (12) */
export function RatingBadge({ avg, count, size = '.8rem' }) {
  if (!avg && !count) return null
  const display = avg ? Number(avg).toFixed(1) : '—'
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '.3rem', fontSize: size }}>
      <span style={{ color: '#f59e0b' }}>★</span>
      <span style={{ fontWeight: 700, color: 'var(--text)' }}>{display}</span>
      {count > 0 && <span style={{ color: 'var(--muted)' }}>({count})</span>}
    </span>
  )
}

/* ── Sub-rating criteria definitions ─────────────────────── */

/** Used when EMPLOYER reviews WORKER */
export const WORKER_SUB_RATINGS = [
  { key: 'subPunctuality',     label: 'Vaxtında gəlmə',        Icon: Clock },
  { key: 'subWorkQuality',     label: 'İş keyfiyyəti',          Icon: Sparkles },
  { key: 'subCommunication',   label: 'Əlaqə',                  Icon: MessageCircle },
  { key: 'subReliability',     label: 'Etibarlılıq',            Icon: Handshake },
  { key: 'subProfessionalism', label: 'Peşəkarlıq',             Icon: Target },
]

/** Used when WORKER reviews EMPLOYER */
export const EMPLOYER_SUB_RATINGS = [
  { key: 'subPayment',         label: 'Ödəniş',                 Icon: Banknote },
  { key: 'subCommunication',   label: 'Əlaqə',                  Icon: MessageCircle },
  { key: 'subFairness',        label: 'Münasibət',              Icon: Scale },
  { key: 'subTaskClarity',     label: 'Tapşırığın aydınlığı',  Icon: ClipboardList },
  { key: 'subWorkEnvironment', label: 'İş mühiti',              Icon: Home },
]

/* ── Interactive sub-rating group (used in forms) ─────────── */
export function SubRatingGroup({ items, values, onChange, size = '1.2rem' }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '.6rem' }}>
      {items.map(({ key, label, Icon }) => (
        <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '.75rem' }}>
          <span style={{
            fontSize: '.8rem', minWidth: 170, color: 'var(--muted)',
            display: 'flex', alignItems: 'center', gap: '.35rem',
          }}>
            <Icon size={13} />
            <span>{label}</span>
          </span>
          <StarRating
            value={values?.[key] || 0}
            onChange={v => onChange(key, v)}
            size={size}
            interactive
          />
          {values?.[key] > 0 && (
            <span style={{ fontSize: '.72rem', color: '#f59e0b', fontWeight: 700, minWidth: 20 }}>
              {values[key]}
            </span>
          )}
        </div>
      ))}
    </div>
  )
}

/* ── Compact sub-rating display (used in review cards) ────── */
export function SubRatingDisplay({ items, values, size = '.75rem' }) {
  const rated = items.filter(i => values?.[i.key] != null && values[i.key] > 0)
  if (!rated.length) return null
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
      gap: '.35rem .75rem',
      marginTop: '.6rem',
      padding: '.6rem .75rem',
      background: 'rgba(255,255,255,.02)',
      border: '1px solid rgba(255,255,255,.05)',
      borderRadius: 'var(--radius-sm)',
    }}>
      {rated.map(({ key, label, Icon }) => (
        <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '.35rem', fontSize: size }}>
          <Icon size={11} color="var(--muted)" />
          <span style={{ color: 'var(--muted)', flex: 1 }}>{label}</span>
          <span style={{ display: 'inline-flex', gap: '1px' }}>
            {[1,2,3,4,5].map(s => (
              <span key={s} style={{ color: s <= values[key] ? '#f59e0b' : 'rgba(255,255,255,.15)', fontSize: '.65rem' }}>★</span>
            ))}
          </span>
        </div>
      ))}
    </div>
  )
}
